"use client";

import React, { useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  Box,
  ChevronLeft,
  ChevronRight,
  FileText,
  LampDesk,
  LogOut,
  MapPin,
  Plug,
  Tablet,
  Users,
} from "lucide-react";
import { CMSModal } from "@/context";
import { twMerge } from "tailwind-merge";
import { useTranslation } from "react-i18next";

interface SubLink {
  name: string;
  link?: string;
  icon?: React.ReactNode;
  description?: string;
  subLinks?: SubLink[];
}

interface NavItem {
  name: string;
  link?: string;
  match?: string;
  subLinks?: SubLink[];
}

const locationItems: SubLink[] = [
  {
    name: "Visitor Settings",
    icon: <LogOut size={18} />,
    subLinks: [
      { name: "Welcome Screen", link: "/settings/visitor/welcome" },
      { name: "Branding & Badges", link: "/settings/visitor/branding-and-badges" },
      { name: "Categories", link: "/settings/visitor/categories" },
      { name: "Fields", link: "/settings/visitor/fields" },
      { name: "Advanced Settings", link: "/settings/visitor/advanced-settings" },
    ],
  },
  {
    name: "Employee Settings",
    icon: <Users size={18} />,
    subLinks: [
      { name: "General", link: "/settings/employee/general" },
      { name: "Pocket", link: "/settings/employee/pocket" },
      { name: "Sign in questions", link: "/settings/employee/signIn" },
      { name: "Sign out messages", link: "/settings/employee/signOut" },
    ],
  },
  {
    name: "Deliveries Settings",
    icon: <Box size={18} />,
    subLinks: [
      { name: "Setup", link: "/settings/deliveries/setup" },
      { name: "Delivery Instructions", link: "/settings/deliveries/instructions" },
    ],
  },
  {
    name: "Spaces Settings",
    icon: <LampDesk size={18} />,
    subLinks: [
      { name: "Spaces", link: "/settings/space/space" },
      { name: "Resources", link: "/settings/space/resources" },
      { name: "Categories", link: "/settings/space/categories" },
    ],
  },
  {
    name: "Devices",
    icon: <Tablet size={18} />,
    link: "/settings/device",
  },
];

const orgItems: SubLink[] = [
  {
    name: "Agreements Library",
    icon: <FileText size={18} />,
    link: "/settings/agreement-library",
  },
  {
    name: "Locations & AddOns",
    icon: <MapPin size={18} />,
    link: "/settings/locations",
  },
  {
    name: "Integrations",
    icon: <Plug size={18} />,
    link: "/settings/integrations",
  },
];

const navLinks: NavItem[] = [
  { name: "visitors", link: "/visitors/timeline", match: "/visitors" },
  { name: "employees", link: "/employees/timeline", match: "/employees" },
  { name: "spaces", link: "/spaces/bookings", match: "/spaces" },
  { name: "deliveries", link: "/delivery", match: "/delivery" },
  {
    name: "settings",
    match: "/settings",
    subLinks: [
      { name: "LOCATION", subLinks: locationItems },
      { name: "ORGANIZATION", subLinks: orgItems },
    ],
  },
];

const MobileNav = () => {
  const { theme } = React.useContext(CMSModal);
  const { t } = useTranslation();
  const [activeMain, setActiveMain] = useState<NavItem | null>(null);
  const [activeSection, setActiveSection] = useState<SubLink | null>(null);
  const [activeSub, setActiveSub] = useState<SubLink | null>(null);
  const pathname = usePathname();

  const goBack = () => {
    if (activeSub) setActiveSub(null);
    else if (activeSection) setActiveSection(null);
    else if (activeMain) setActiveMain(null);
  };

  const isPathActive = (item: NavItem | SubLink): boolean => {
    if (item.link && pathname.startsWith(item.link)) return true;
    if (item.subLinks && item.subLinks.length > 0) {
      return item.subLinks.some((sub) => isPathActive(sub));
    }
    return false;
  };

  const isDark = theme === "dark";

  const rowClass = twMerge(
    "w-full flex items-center justify-between px-5 py-4 transition-colors",
    isDark
      ? "border-b border-gray-700 hover:bg-[#262727]"
      : "border-b border-gray-100 hover:bg-gray-50",
  );

  const activeTextClass = "text-[#f74000]";
  const defaultTextClass = isDark ? "text-gray-100" : "text-gray-800";

  return (
    <div
      className={twMerge(
        "w-full h-screen flex flex-col lg:hidden overflow-y-auto",
        isDark ? "bg-[#191919]" : "bg-white",
      )}
    >
      {/* Header */}
      <div
        className={twMerge(
          "flex items-center px-5 py-4",
          isDark ? "border-b border-gray-700" : "border-b border-gray-200",
        )}
      >
        {activeMain ? (
          <button
            onClick={goBack}
            className={twMerge("flex items-center gap-1 text-sm font-medium", defaultTextClass)}
          >
            <ChevronLeft size={18} />
            Back
          </button>
        ) : (
          <h2 className={twMerge("text-base font-semibold", defaultTextClass)}>Menu</h2>
        )}
      </div>

      {/* Main nav */}
      {!activeMain && (
        <div className="flex flex-col">
          {navLinks.map((item) =>
            item.subLinks ? (
              <button
                key={item.name}
                onClick={() => setActiveMain(item)}
                className={twMerge(rowClass, isPathActive(item) ? activeTextClass : defaultTextClass)}
              >
                <span className="text-sm font-semibold capitalize">{t(`header.${item.name}`)}</span>
                <ChevronRight size={18} className="text-gray-400" />
              </button>
            ) : (
              <Link
                key={item.name}
                href={item.link ?? "#"}
                className={twMerge(rowClass, isPathActive(item) ? activeTextClass : defaultTextClass)}
              >
                <span className="text-sm font-semibold capitalize">{t(`header.${item.name}`)}</span>
                <ChevronRight size={18} className="text-gray-400" />
              </Link>
            ),
          )}
        </div>
      )}

      {/* Settings: two sections (LOCATION / ORGANIZATION) */}
      {activeMain && !activeSection && (
        <div className="flex flex-col">
          {activeMain.subLinks?.map((section) => (
            <div key={section.name}>
              <p className="px-5 pt-5 pb-2 text-xs font-semibold text-gray-400 tracking-widest">
                {section.name}
              </p>
              {section.subLinks?.map((item) =>
                item.subLinks && item.subLinks.length > 0 ? (
                  <button
                    key={item.name}
                    onClick={() => setActiveSection(item)}
                    className={twMerge(
                      rowClass,
                      isPathActive(item) ? activeTextClass : defaultTextClass,
                    )}
                  >
                    <span className="flex items-center gap-3 text-sm font-medium">
                      {item.icon}
                      {item.name}
                    </span>
                    <ChevronRight size={18} className="text-gray-400" />
                  </button>
                ) : (
                  <Link
                    key={item.name}
                    href={item.link ?? "#"}
                    className={twMerge(
                      rowClass,
                      isPathActive(item) ? activeTextClass : defaultTextClass,
                    )}
                  >
                    <span className="flex items-center gap-3 text-sm font-medium">
                      {item.icon}
                      {item.name}
                    </span>
                    <ChevronRight size={18} className="text-gray-400" />
                  </Link>
                ),
              )}
            </div>
          ))}
        </div>
      )}

      {/* Sub-links (e.g. Visitor Settings → Welcome Screen) */}
      {activeSection && !activeSub && (
        <div className="flex flex-col">
          <p className="px-5 pt-5 pb-2 text-xs font-semibold text-gray-400 tracking-widest">
            {activeSection.name.toUpperCase()}
          </p>
          {activeSection.subLinks?.map((item) =>
            item.subLinks && item.subLinks.length > 0 ? (
              <button
                key={item.name}
                onClick={() => setActiveSub(item)}
                className={twMerge(
                  rowClass,
                  isPathActive(item) ? activeTextClass : defaultTextClass,
                )}
              >
                <span className="flex items-center gap-3 text-sm font-medium">
                  {item.icon}
                  {item.name}
                </span>
                <ChevronRight size={18} className="text-gray-400" />
              </button>
            ) : (
              <Link
                key={item.name}
                href={item.link ?? "#"}
                className={twMerge(
                  rowClass,
                  isPathActive(item) ? activeTextClass : defaultTextClass,
                )}
              >
                <span className="flex items-center gap-3 text-sm">{item.name}</span>
                <ChevronRight size={18} className="text-gray-400" />
              </Link>
            ),
          )}
        </div>
      )}

      {/* Deep links */}
      {activeSub && (
        <div className="flex flex-col">
          <p className="px-5 pt-5 pb-2 text-xs font-semibold text-gray-400 tracking-widest">
            {activeSub.name.toUpperCase()}
          </p>
          {activeSub.subLinks?.map((item) => (
            <Link
              key={item.name}
              href={item.link ?? "#"}
              className={twMerge(
                rowClass,
                isPathActive(item) ? activeTextClass : defaultTextClass,
              )}
            >
              <span className="text-sm">{item.name}</span>
              <ChevronRight size={18} className="text-gray-400" />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default MobileNav;
